import { Brand } from '@shared/layout';
import { IconArrowRight } from '@shared/ui';

/**
 * Shown when the shared Atelier session is missing or expired.
 *
 * Deliberately **not** a login form. The Backoffice has no credentials of its
 * own — Atelier runs Google SSO once for every internal tool, and its session
 * cookie is what authenticates us. So all this screen can do is send the
 * employee to the Hub, where they either already have a session (and come
 * straight back) or sign in once.
 *
 * `hubUrl` is passed in by the route rather than read here, so this component
 * stays a pure render. It is a public build-time value (`VITE_ATELIER_URL`) —
 * there is nothing to protect about the hub's origin, and the session secret
 * that once justified resolving it server-side no longer exists in this app.
 */
export function SignInPage({ hubUrl }: { hubUrl: string }) {
    return (
        <main className="flex min-h-screen items-center justify-center bg-gray-50 px-6">
            <div className="w-full max-w-md rounded-xl border border-gray-200 bg-white p-8 shadow-card">
                <Brand />

                <h1 className="mt-7 text-xl font-semibold tracking-tight text-gray-900">
                    Sign in through Atelier
                </h1>
                <p className="mt-2 text-[13.5px] leading-relaxed text-gray-500">
                    The Backoffice uses the same session as the rest of Veevart&apos;s internal
                    tools. Open the Atelier hub and pick <strong>Backoffice</strong>
                    {' — '}
                    if you&apos;re already signed in with your Veevart Google account, you come
                    straight back here.
                </p>

                <a
                    href={hubUrl}
                    className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-lg border border-transparent bg-brand px-4 py-2.5 text-[13.5px] font-semibold text-white shadow-sm transition-colors hover:bg-brand-600"
                >
                    Continue to Atelier
                    <IconArrowRight className="size-4" />
                </a>

                <p className="mt-5 border-t border-gray-100 pt-5 text-xs leading-relaxed text-gray-400">
                    Signed in already and still seeing this? Your session may have expired after 8
                    hours, or your account may not have access to this tool yet — ask in
                    #engineering.
                </p>
            </div>
        </main>
    );
}
